import React, { useEffect, useState } from "react";
import { Search, Briefcase, BellRing, ArrowRight } from "lucide-react";
import { useNotifications } from "@/contexts/NotificationContext";

const SEEN_KEY = "jobdew_tutorial_seen";

const STEPS = [
  {
    icon: Search,
    title: "Find work near you",
    body: "Browse jobs by city and category, or search by keyword — new listings are added every day.",
  },
  {
    icon: Briefcase,
    title: "Hiring? Post in minutes",
    body: "Local shops and businesses can post their first job for free — it only takes a couple of minutes.",
  },
  {
    icon: BellRing,
    title: "Never miss a match",
    body: "Turn on notifications and we'll let you know the moment a job — or a candidate — is a good fit.",
    cta: "enable-notifications",
  },
];

function isStandaloneDisplay() {
  return (
    (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
    window.navigator?.standalone === true
  );
}

export default function InstallTutorial() {
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);
  const notifications = useNotifications();

  useEffect(() => {
    if (isStandaloneDisplay() && !localStorage.getItem(SEEN_KEY)) {
      setVisible(true);
    }
  }, []);

  const finish = () => {
    localStorage.setItem(SEEN_KEY, "1");
    setVisible(false);
  };

  if (!visible) return null;

  const current = STEPS[step];
  const Icon = current.icon;
  const isLast = step === STEPS.length - 1;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.7)" }}>
      <div className="bg-white rounded-3xl w-full max-w-sm p-7 text-center shadow-2xl">
        <button
          type="button"
          onClick={() => finish()}
          className="ml-auto block text-xs text-slate-400 hover:text-slate-600 mb-4"
          data-testid="tutorial-skip"
        >
          Skip
        </button>

        <div className="w-16 h-16 rounded-2xl bg-black text-white flex items-center justify-center mx-auto mb-5">
          <Icon className="w-7 h-7" />
        </div>
        <h3 className="text-xl font-bold text-slate-900">{current.title}</h3>
        <p className="text-slate-500 text-sm mt-2 leading-relaxed">{current.body}</p>

        <div className="flex items-center justify-center gap-1.5 mt-6 mb-6">
          {STEPS.map((_, i) => (
            <span key={i} className={`h-1.5 rounded-full transition-all ${i === step ? "w-6 bg-black" : "w-1.5 bg-slate-200"}`} />
          ))}
        </div>

        {current.cta === "enable-notifications" ? (
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={() => {
                if (notifications?.subscribeToPush) notifications.subscribeToPush().finally(() => finish());
                else if ("Notification" in window) Notification.requestPermission().finally(() => finish());
                else finish();
              }}
              className="w-full bg-black hover:bg-slate-800 text-white font-semibold py-2.5 rounded-full"
            >
              Enable notifications
            </button>
            <button type="button" onClick={() => finish()} className="text-xs text-slate-400 hover:text-slate-600">
              Maybe later
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setStep(s => Math.min(STEPS.length - 1, s + 1))}
            className="w-full bg-black hover:bg-slate-800 text-white font-semibold py-2.5 rounded-full flex items-center justify-center gap-1.5"
            data-testid="tutorial-next"
          >
            {isLast ? "Get started" : "Next"} <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
